import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { isFinite, toNumber } from 'lodash';
import { useMemo } from 'react';
import { MAX_ROWS_PER_PAGE } from 'src/constants';
import { ILayoutType } from 'src/interfaces';
import {
  ILocationGroup,
  ILocationGroupTabList,
} from 'src/interfaces/LocationGroup';
import { generateQuery } from 'src/utils';
import { queryKeys } from 'src/utils/queryKeys';

import { requestCCApi } from '../../../utils/axios-utils';

export const useGetLocationGroup = (
  locationGroupId = '',
  orientation = false,
) => {
  return useQuery({
    queryKey: queryKeys.locationGroups.details([
      locationGroupId,
      `orientation=${orientation}`,
    ]),
    queryFn: () => fetchLocationGroup(locationGroupId, orientation),
    enabled: Boolean(locationGroupId) && isFinite(toNumber(locationGroupId)),
  });
};

const fetchLocationGroup = async (
  locationGroupId: string,
  orientation?: boolean,
) => {
  const url = `/locationGroups/${locationGroupId}?include=layoutTypes${
    orientation ? ',orientation' : ''
  }`;

  const response = await requestCCApi({ url });

  const baseData = response.data;

  const layoutTypes = response?.includes?.layoutTypes?.data ?? [];

  const layoutTypeData = layoutTypes.find(
    (item: ILayoutType) => item.id === baseData.layout_type_id,
  );

  return {
    ...baseData,
    layoutType: layoutTypeData,
  } as ILocationGroup;
};

export type PaginatedLGResponse = {
  locationGroups: ILocationGroupTabList[];
  totalPages: number;
  totalCount: number;
};

export const useGetPaginatedLocationGroupsList = (
  filters: string[] = [],
  enabled: boolean = true,
  queryOverwrite: UseQueryOptions<PaginatedLGResponse> = {},
) => {
  // Build params without duplicating page/sort/pageSize coming from filters
  const queryParams = useMemo(() => {
    const f = (filters || []).filter(Boolean);

    const has = (k: string) => f.some((s) => s.startsWith(`${k}=`));

    const base: string[] = [];
    if (!has('sort')) base.push('sort=name');
    if (!has('page')) base.push('page=1');
    if (!has('pageSize'))
      base.push(`pageSize=${Number(MAX_ROWS_PER_PAGE) || 50}`);

    return [...f, ...base];
  }, [filters]);

  return useQuery<PaginatedLGResponse>({
    queryKey:
      queryKeys.getLocationGroupsWithLocationCount.pagination(queryParams),
    queryFn: async () => {
      const url = `/getLocationGroupsWithLocationCount${generateQuery(
        [],
        queryParams,
      )}`;
      const response = await requestCCApi({ url });

      return {
        locationGroups: response.data as ILocationGroupTabList[],
        totalPages: Number(
          response.totalPages ?? response?.meta?.totalPages ?? 0,
        ),
        totalCount: Number(
          response.totalCount ?? response?.meta?.totalCount ?? 0,
        ),
      };
    },

    enabled,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    keepPreviousData: false,
    structuralSharing: false,
    cacheTime: 0,
    ...queryOverwrite,
  });
};
